import { gtmEvent } from '../../../utils/gtmEvents';
import actionStrategy from '../stategies/actionStrategy';
import conditionStrategy from '../stategies/conditionStrategy';
import uiStrategy from '../stategies/uiStrategy';
import flattenObject from '../utils';

// The logic to ignore form redirect expects this value
export const IGNORE_REDIRECT_FOR_FORM = 'Ignore Redirect URL';

/**
 * Check all the applicable conditions to a field and apply the actions
 * @param {string | number} value - the value of 'a' form field which should be checked for all field's condition
 * @param {object} fieldCondition - current field condition containing amongst others a conditions and actions attribute
 * @param {string} fieldKey - the fieldKey for the current element under consideration
 * @returns {Array} all possible actions for the field with fieldKey
 */
export const evaluateCondition = (value, fieldCondition, fieldKey) => {
  if (!fieldKey) return [];
  const isValidCondition = conditions => {
    if (!conditions) return null;
    const availableConditions = condition => conditionStrategy({ condition, value });
    const conditionsForGivenKey = condition => condition.key === fieldKey;
    const conditionals = conditions.map(availableConditions).filter(conditionsForGivenKey);
    // no conditional added for this element
    if (!conditionals.length) return null;
    return conditionals.every(c => c.value);
  };
  const elementActions = (actions, conditionValue) => {
    if (!actions) return [];
    const availableActions = action => actionStrategy({ action, conditionValue });
    return actions.map(availableActions);
  };

  const maybeValid = isValidCondition(fieldCondition.conditions);
  if (maybeValid === null) return [];
  return elementActions(fieldCondition.actions, maybeValid);
};

/**
 * Check all the conditions for this field's item compare values
 * @param {string | number} value - the value of 'a' form field which should be checked for all field's condition
 * @param {Array} fieldConditions - all conditions applicable to this field
 * @param {string} fieldKey - the fieldKey for the current element under consideration
 * @returns {Array} all possible actions for the field with fieldKey
 */
const evaluateConditionsForFieldKey = (value, fieldConditions, fieldKey) =>
  flattenObject(fieldConditions, {
    execute: fieldCondition => evaluateCondition(value, fieldCondition, fieldKey),
  });

/**
 * Check the fieldKey against all conditions
 * for each field element and match their values
 * @param {Array} elements - all fields of the form
 * @param {string | number} value - the value of 'a' form field which should be checked for all field's condition
 * @param {string} fieldKey - the fieldKey for the current element under consideration
 * @returns {Array} all possible actions for the field with fieldKey
 */
const evaluateElementConditions = (elements, value, fieldKey) =>
  flattenObject(elements, {
    execute: ({ model: { conditionSettings } }) =>
      evaluateConditionsForFieldKey(value, conditionSettings.fieldConditions, fieldKey),
  });

export const runConditionalForElements = form => {
  if (!form) return;
  const elements = form.fields;

  const getConditionalForEachElement = ({ model: { value, conditionSettings } }) =>
    evaluateElementConditions(elements, value, conditionSettings.fieldKey);

  const conditionalActions = flattenObject(elements, {
    execute: getConditionalForEachElement,
  });

  const modifyItemWithConditionalActions = element => {
    const foundElementsInActions = conditionalActions.filter(c => c.key === element.model.conditionSettings.fieldKey);
    if (!foundElementsInActions) return element;
    return { ...element, model: { ...element.model, actions: foundElementsInActions } };
  };
  return { ...form, fields: elements.map(modifyItemWithConditionalActions) };
};

// Get all name value pairs as a single object
export const getGTMPayload = ({ fields }) => {
  if (!fields) return;
  return flattenObject(
    fields,
    {
      execute: ({ model: { name, value } }) => {
        if (value === undefined) return {};
        return { [name]: value };
      },
    },
    {},
  );
};

export const updateFormWithFieldValues = (form, fieldValues) => {
  const { fields } = form;
  const updateField = field => {
    const correspondingValue = fieldValues?.find(f => f.fieldName === field.valueField?.name);
    if (!correspondingValue) return field;
    return { ...field, model: { ...field.model, value: correspondingValue.state.value } };
  };
  return { ...form, fields: fields.map(updateField) };
};

export const isFormValid = fieldValues => fieldValues.every(f => f.state.isValid);

export const getFieldConditionalactions = ({ field, form, fieldValues }) => {
  const updatedFormValues = updateFormWithFieldValues(form, fieldValues);
  const formWithUpdatedconditionals = runConditionalForElements(updatedFormValues);
  return (
    formWithUpdatedconditionals.fields.find(f => f.valueField?.name === field.valueField?.name)?.model?.actions || []
  );
};

export const getUIStrategy = ({ field, form, fieldValues }) => {
  const actions = getFieldConditionalactions({ field, form, fieldValues });
  return uiStrategy({ actions });
};

export const getFieldValuesFromData = data =>
  data.map(({ key, value }) => ({
    fieldName: key,
    state: { value },
  }));

export const prepareGTMPayload = ({ data, form, gtmEventName }) => {
  if (!data) return;
  if (!form) return;
  const fieldValues = getFieldValuesFromData(data);
  const updatedFormValues = updateFormWithFieldValues(form, fieldValues);
  const gtmPayload = getGTMPayload(updatedFormValues);
  if (!gtmPayload) return;
  return { event: gtmEventName, ...gtmPayload };
};

const sendGTMEvent = ({ data, form, gtmEventName }) => {
  const gtmPayload = prepareGTMPayload({ data, form, gtmEventName });
  if (!gtmPayload) return;
  gtmEvent(gtmPayload);
};

/**
 * Custom form fetcher so we can handle success for form via onRedirect props
 * This receives the gtmevent name and returns the form fetcher
 * @param {string} gtmEventName - Event name
 * @param {FormData} formData - form data from sitecore
 * @param {string} endpoint - action of the form
 * @returns {Function} - The form fetcher
 */
export const formFetcher =
  ({ gtmEventName }) =>
  (formData, endpoint) =>
    fetch(endpoint, {
      body: formData.toUrlEncodedFormData(),
      method: 'post',
      // IMPORTANT: Sitecore forms relies on cookies for some state management, so credentials must be included.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then(res => res.json())
      // A Patch - When no redirect url is supplied, add specific url
      // so that we do not rewrite the submit logic for sitecore react forms
      // and use the onRedirect props to handle form success
      // Since we need the form data to send the GTM Event, we also do that here
      .then(result => {
        const { data } = formData;
        const { nextForm } = result;
        sendGTMEvent({ data, form: nextForm, gtmEventName });
        // Pass result down to sitecore success handler
        return { ...result, redirectUrl: result.redirectUrl || IGNORE_REDIRECT_FOR_FORM };
      })
      // Catch errors not handled and show friendly error
      .catch(() => ({
        success: false,
        errors: 'Something went wrong. We could not submit form.',
      }));
